import { CORE_R, PETAL_D } from './geometry';

/*
  Геометрия веера сервисов («Мои сервисы», spec-4 §3) — чистые функции, без React.

  Пластина — ровно PETAL_D знака: твёрдая, одного размера при любом числе сервисов, поворачивается
  вокруг заклёпки. Система координат — как у знака: единицы PETAL_D, пластина с углом 0 стоит на 6 часах,
  плюс — по часовой стрелке. svg веера — viewBox −520…520: запас под отрыв активной пластины.

  - До шести пластин — кадры раскрытия знака (шаг 60°), при шести — точный знак Kaname.
  - С семи шаг 360/N, следующая пластина ложится поверх предыдущей черепицей; на 6 часах шов: копии
    первых пластин поверх хвостовых, чтобы черепица шла по кругу без начала и конца.
  - Наведение раздвигает соседей поворотом; активная не поворачивается, а немного отрывается от заклёпки
    вдоль своей оси (FAN_LIFT) — как вынутая из веера пластина.
  - Попадание — полярное, без DOM: по целевым углам (с раздвиганием и отрывом), отсюда гистерезис без
    дребезга.
*/

/** Половина стороны svg веера, в единицах пластины. */
export const FAN_HALF = 520;
/** Кольцо заклёпки — как у знака с ушедшим ядром. */
export const FAN_RING_R = CORE_R - 6;
/** Ближе к центру — кольцо, дальше — мимо веера. */
export const FAN_HIT_IN = 100;
export const FAN_HIT_OUT = 500;
/**
 * Отрыв активной пластины от заклёпки вдоль её оси, в единицах: ≈ 11 px при D 520, с размером веера
 * меняется сам. Кончик отошедшей — на r ≈ 510, внутри viewBox.
 */
export const FAN_LIFT = 22;
/** Круг света пластины: тьма (внутренний конец пластины на r ≈ 111) и полный свет. */
export const FAN_LIGHT_OFF = 100;
export const FAN_LIGHT_ON = 500;

/** Пластина целиком лежит в кольце радиусов 112…486: полярная таблица считается только там. */
const SPAN_IN = 112;
const SPAN_OUT = 486;
const SPAN_STEP = 2;
/** На сколько частей дробится каждый Q-сегмент контура. */
const Q_PARTS = 20;
/** Неполный веер: «липкое» поле за последней пластиной и перед первой, градусы. */
const PARTIAL_SLACK = 5;
/** Шов на 6 часах покрывает пластины на этот угол: шире самой широкой полосы пластины (≈ 57°). */
const SEAM_REACH = 64;
/** Раздвигание: соседи на расстоянии 1, 2, 3 отходят на эти доли амплитуды. */
const PART_WEIGHTS = [1, 0.55, 0.2] as const;
/** Якорь кончика пластины для полётов помощника. */
const TIP_R = 470;
const TIP_SHIFT = -6;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const mod = (v: number, m: number) => ((v % m) + m) % m;
const DEG = 180 / Math.PI;

/** Полярный угол точки: 0 на 6 часах, плюс по часовой стрелке. */
function polar(x: number, y: number): number {
  return Math.atan2(-x, y) * DEG;
}

/** Контур PETAL_D ломаной: Q-сегменты дробятся на parts частей. Замкнут (Z), последняя точка = первая. */
function flatten(parts: number): [number, number][] {
  const t = PETAL_D.replace(/[MQZ]/g, ' ').trim().split(/\s+/).map(Number);
  const pts: [number, number][] = [];
  let x0 = t[0];
  let y0 = t[1];
  for (let i = 2; i + 3 < t.length; i += 4) {
    const [cx, cy, x1, y1] = [t[i], t[i + 1], t[i + 2], t[i + 3]];
    for (let s = 0; s < parts; s++) {
      const u = s / parts;
      const a = (1 - u) * (1 - u);
      const b = 2 * (1 - u) * u;
      const c = u * u;
      pts.push([a * x0 + b * cx + c * x1, a * y0 + b * cy + c * y1]);
    }
    x0 = x1;
    y0 = y1;
  }
  return pts;
}

/*
  Ось пластины — от заклёпки к центру тяжести её контура: вдоль неё активная отрывается от заклёпки.
  Считается раз при загрузке модуля (площадь многоугольника по ломаной PETAL_D); ≈ 17° от 6 часов.
*/
const AXIS = (() => {
  const poly = flatten(Q_PARTS);
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, y0] = poly[i];
    const [x1, y1] = poly[(i + 1) % poly.length];
    const c = x0 * y1 - x1 * y0;
    a += c;
    cx += (x0 + x1) * c;
    cy += (y0 + y1) * c;
  }
  return polar(cx / (3 * a), cy / (3 * a));
})();

/*
  Таблица краёв пластины по радиусу: lo — задний край (меньший угол), hi — передний. Считается при
  загрузке модуля из самого PETAL_D — пересечением окружности с ломаной контура; готовыми числами
  не хранится. lo идёт от ≈ +41° на r 140 до ≈ −12° на r 470: пластина закручена.
*/
const SPAN = (() => {
  const poly = flatten(Q_PARTS);
  const lo: number[] = [];
  const hi: number[] = [];
  for (let r = SPAN_IN; r <= SPAN_OUT; r += SPAN_STEP) {
    let a = Infinity;
    let b = -Infinity;
    for (let i = 0; i < poly.length; i++) {
      const [x0, y0] = poly[i];
      const [x1, y1] = poly[(i + 1) % poly.length];
      const dx = x1 - x0;
      const dy = y1 - y0;
      const qa = dx * dx + dy * dy;
      const qb = 2 * (x0 * dx + y0 * dy);
      const qc = x0 * x0 + y0 * y0 - r * r;
      const disc = qb * qb - 4 * qa * qc;
      if (qa === 0 || disc < 0) continue;
      const sq = Math.sqrt(disc);
      for (const t of [(-qb - sq) / (2 * qa), (-qb + sq) / (2 * qa)]) {
        if (t < 0 || t > 1) continue;
        const f = polar(x0 + t * dx, y0 + t * dy);
        a = Math.min(a, f);
        b = Math.max(b, f);
      }
    }
    // Окружность всегда пересекает пластину в этом кольце; на всякий случай — край соседа.
    lo.push(Number.isFinite(a) ? a : (lo.at(-1) ?? 0));
    hi.push(Number.isFinite(b) ? b : (hi.at(-1) ?? 0));
  }
  return { lo, hi };
})();

/** Углы заднего и переднего края пластины с углом 0 на радиусе r (за кольцом 112…486 — край кольца). */
export function petalSpan(r: number): { lo: number; hi: number } {
  const i = (clamp(r, SPAN_IN, SPAN_OUT) - SPAN_IN) / SPAN_STEP;
  const i0 = Math.floor(i);
  const i1 = Math.min(i0 + 1, SPAN.lo.length - 1);
  const f = i - i0;
  return {
    lo: SPAN.lo[i0] + (SPAN.lo[i1] - SPAN.lo[i0]) * f,
    hi: SPAN.hi[i0] + (SPAN.hi[i1] - SPAN.hi[i0]) * f,
  };
}

/** Пластина с углом angle содержит точку (x, y): полярная таблица краёв, без DOM. */
function inPetal(x: number, y: number, angle: number): boolean {
  const r = Math.hypot(x, y);
  if (r < SPAN_IN || r > SPAN_OUT) return false;
  const { lo, hi } = petalSpan(r);
  return mod(polar(x, y) - angle - lo, 360) <= hi - lo;
}

/** Сдвиг пластины с углом angle, отошедшей от заклёпки на lift единиц вдоль своей оси. */
export function liftOffset(angle: number, lift = FAN_LIFT): { x: number; y: number } {
  return polarPoint(lift, angle + AXIS);
}

/** Шаг веера: до шести — 60°, как у знака; дальше — 360/N. */
export function fanStep(n: number): number {
  return n <= 6 ? 60 : 360 / n;
}

/** Места пластин без раздвигания: k·шаг от 6 часов по часовой стрелке. */
export function fanAngles(n: number): number[] {
  const step = fanStep(n);
  return Array.from({ length: Math.max(0, n) }, (_, k) => k * step);
}

/** Сколько головных пластин копируется поверх хвостовых у шва на 6 часах (только при N ≥ 7). */
export function seamCount(n: number): number {
  if (n < 7) return 0;
  return Math.min(Math.ceil(SEAM_REACH / fanStep(n)), Math.floor(n / 2));
}

/** g — половина ночной обводки под заливкой пластины (шов между пластинами), в единицах; при N ≤ 6 — 0. */
export function seamWidth(n: number): number {
  if (n < 7) return 0;
  return clamp(fanStep(n) / 2, 6, 10);
}

/** Амплитуда раздвигания: 10° до N = 24, 9° при 30, 7,5° при 36, 6° при 48 и больше. */
export function partAmp(n: number): number {
  return clamp(0.75 * fanStep(n), 6, 10);
}

/**
 * Смещения раздвигания для активной пластины k: соседи на расстоянии 1, 2, 3 отходят от неё
 * на ±p·[1; 0,55; 0,2]. При N ≥ 6 — по кругу через шов, не больше ⌊(N−1)/2⌋ с каждой стороны;
 * при N < 6 — по индексу, без заворота через пустое место. Сама k не смещается.
 */
export function partOffsets(n: number, k: number): number[] {
  const out = Array.from({ length: Math.max(0, n) }, () => 0);
  if (k < 0 || k >= n) return out;
  const p = partAmp(n);
  if (n >= 6) {
    const reach = Math.min(PART_WEIGHTS.length, Math.floor((n - 1) / 2));
    for (let d = 1; d <= reach; d++) {
      out[(k + d) % n] += p * PART_WEIGHTS[d - 1];
      out[(k - d + n) % n] -= p * PART_WEIGHTS[d - 1];
    }
    return out;
  }
  for (let d = 1; d <= PART_WEIGHTS.length; d++) {
    if (k + d < n) out[k + d] = p * PART_WEIGHTS[d - 1];
    if (k - d >= 0) out[k - d] = -p * PART_WEIGHTS[d - 1];
  }
  return out;
}

/** Целевые углы: места пластин плюс раздвигание вокруг активной (k = −1 — покой). */
export function fanTargets(n: number, k = -1): number[] {
  const offsets = partOffsets(n, k);
  return fanAngles(n).map((a, i) => a + offsets[i]);
}

/**
 * Лежит ли пластина s поверх пластины k там, где они перекрываются. До шести следующая под предыдущей,
 * с семи — поверх; у шва копии головных пластин лежат поверх хвостовых.
 */
function above(n: number, s: number, k: number): boolean {
  if (n <= 6) return s < k;
  const m = seamCount(n);
  if (s < m && k >= n - m) return true;
  if (k < m && s >= n - m) return false;
  return s > k;
}

/**
 * Попадание по точке (x, y) в единицах svg веера: индекс пластины, 'ring' — кольцо заклёпки, null — мимо.
 * angles — целевые углы (с раздвиганием), а не текущие анимированные: так у активной пластины
 * запас шириной в амплитуду раздвигания, и проводка по вееру не дребезжит.
 *
 * Видимая часть пластины k — от её заднего края до заднего края следующей (следующая лежит поверх).
 * Зазоры неполного веера «липкие»: относятся к пластине перед зазором.
 *
 * active — активная пластина, отошедшая от заклёпки на FAN_LIFT: ей остаётся и прежний сектор (внешняя
 * граница дальше на отрыв), и место, куда она отошла, если там её не накрывает пластина выше. Полоса у
 * заднего края, которую она открыла, отходя, — тоже её: тот же запас, что у раздвигания.
 */
export function hitTest(
  n: number,
  angles: readonly number[],
  x: number,
  y: number,
  active = -1,
): number | 'ring' | null {
  const r = Math.hypot(x, y);
  if (r < FAN_HIT_IN) return 'ring';
  if (n <= 0) return null;
  const lifted = active >= 0 && active < n;
  if (r > FAN_HIT_OUT + (lifted ? FAN_LIFT : 0)) return null;
  const s = sector(n, angles, x, y, r);
  if (!lifted) return s;
  if (s === active) return active;
  const o = liftOffset(angles[active]);
  if (!inPetal(x - o.x, y - o.y, angles[active])) return r > FAN_HIT_OUT ? null : s;
  if (s !== null && r <= FAN_HIT_OUT && above(n, s, active) && inPetal(x, y, angles[s])) return s;
  return active;
}

/** Сектор пластины по точке на радиусе r (без отрыва): задний край k — задний край следующей. */
function sector(n: number, angles: readonly number[], x: number, y: number, r: number): number | null {
  const { lo, hi } = petalSpan(r);
  const base = angles[0];
  // Угол точки от заднего края первой пластины на этом радиусе.
  const v = mod(polar(x, y) - lo - base, 360);
  if (n >= 6) {
    for (let k = 0; k < n - 1; k++) if (v < angles[k + 1] - base) return k;
    return n - 1;
  }
  if (v > 360 - PARTIAL_SLACK) return 0;
  for (let k = 0; k < n - 1; k++) if (v < angles[k + 1] - base) return k;
  return v <= angles[n - 1] - base + (hi - lo) + PARTIAL_SLACK ? n - 1 : null;
}

/** Кончик пластины k: якорь для полётов помощника (Ф4, этап D). У активной (active) — с отрывом. */
export function tipPoint(
  n: number,
  k: number,
  angles: readonly number[] = fanAngles(n),
  active = -1,
): { x: number; y: number } {
  const a = ((angles[k] ?? 0) + TIP_SHIFT) / DEG;
  const o = k === active ? liftOffset(angles[k] ?? 0) : { x: 0, y: 0 };
  return { x: -TIP_R * Math.sin(a) + o.x, y: TIP_R * Math.cos(a) + o.y };
}

/** Точка в системе пластины с углом 0 на радиусе r под углом deg. */
export function polarPoint(r: number, deg: number): { x: number; y: number } {
  const a = deg / DEG;
  return { x: -r * Math.sin(a), y: r * Math.cos(a) };
}

/**
 * Размер веера на сцене (spec-4 §2.4). W — внутренняя ширина сцены, H — сколько остаётся по высоте окна.
 * Рядом с колонкой (W ≥ 704): D = clamp(320, min(W − 352, H), 520). Стопкой: D = clamp(280, min(W, H − 320), 480)
 * — колонка (288) и зазор (32) под веером тоже должны поместиться, иначе карточка наведения уходит за край.
 */
export function fanSize({ W, H }: { W: number; H: number }): { D: number; layout: 'side' | 'stacked' } {
  if (W >= 704) return { D: Math.round(clamp(Math.min(W - 320 - 32, H), 320, 520)), layout: 'side' };
  return { D: Math.round(clamp(Math.min(W, H - 288 - 32, 480), 280, 480)), layout: 'stacked' };
}

/*
  Контур пластины, раздвинутый наружу на off единиц, — для обрезки копий у шва. Обрезка точно по
  контуру хвостовых пластин оставляет на головной пластине их ночной шов и светлую кромку (они
  лежат снаружи контура): обрезка шире на g и на кромку — и шов чистый.
*/
const CONTOUR = flatten(10);
const CONTOUR_SIGN = (() => {
  let area = 0;
  for (let i = 0; i < CONTOUR.length; i++) {
    const [x0, y0] = CONTOUR[i];
    const [x1, y1] = CONTOUR[(i + 1) % CONTOUR.length];
    area += x0 * y1 - x1 * y0;
  }
  return area > 0 ? 1 : -1;
})();
const contours = new Map<number, string>();

export function petalContour(off: number): string {
  const key = Math.round(off * 2) / 2;
  const hit = contours.get(key);
  if (hit) return hit;
  const len = CONTOUR.length;
  const normal = (i: number): [number, number] => {
    const [x0, y0] = CONTOUR[i];
    const [x1, y1] = CONTOUR[(i + 1) % len];
    const d = Math.hypot(x1 - x0, y1 - y0) || 1;
    return [(CONTOUR_SIGN * (y1 - y0)) / d, (-CONTOUR_SIGN * (x1 - x0)) / d];
  };
  const out = CONTOUR.map(([x, y], i) => {
    const [ax, ay] = normal((i - 1 + len) % len);
    const [bx, by] = normal(i);
    let nx = ax + bx;
    let ny = ay + by;
    const m = Math.hypot(nx, ny) || 1;
    nx /= m;
    ny /= m;
    // Острый угол — длиннее по биссектрисе, но не больше чем вдвое.
    const s = key / Math.max(0.5, nx * bx + ny * by);
    return `${(x + nx * s).toFixed(1)} ${(y + ny * s).toFixed(1)}`;
  });
  const d = `M ${out.join(' L ')} Z`;
  contours.set(key, d);
  return d;
}

/**
 * Сектор попадания пластины (для отладки в галерее): полоса между задним краем пластины from и задним
 * краем следующей to; у последней пластины неполного веера — до её переднего края плюс запас.
 */
export function sectorPath(from: number, to: number | { tail: number }): string {
  const rs = [FAN_HIT_IN];
  for (let r = SPAN_IN; r <= SPAN_OUT; r += SPAN_STEP * 4) rs.push(r);
  rs.push(SPAN_OUT, FAN_HIT_OUT);
  const pt = (r: number, deg: number) => {
    const p = polarPoint(r, deg);
    return `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  };
  const end = (r: number) => {
    const s = petalSpan(r);
    return typeof to === 'number' ? to + s.lo : to.tail + s.hi + PARTIAL_SLACK;
  };
  // Дуга по окружности r от угла a до b — точками через 3°.
  const arc = (r: number, a: number, b: number) => {
    const n = Math.max(1, Math.ceil(Math.abs(b - a) / 3));
    return Array.from({ length: n - 1 }, (_, i) => pt(r, a + ((b - a) * (i + 1)) / n));
  };
  const near = rs.map((r) => pt(r, from + petalSpan(r).lo));
  const far = [...rs].reverse().map((r) => pt(r, end(r)));
  const outer = arc(FAN_HIT_OUT, from + petalSpan(FAN_HIT_OUT).lo, end(FAN_HIT_OUT));
  const inner = arc(FAN_HIT_IN, end(FAN_HIT_IN), from + petalSpan(FAN_HIT_IN).lo);
  return `M ${[...near, ...outer, ...far, ...inner].join(' L ')} Z`;
}

/** Запас «липкого» поля неполного веера — для отрисовки секторов. */
export const FAN_PARTIAL_SLACK = PARTIAL_SLACK;
